import AspectRatio from 'components/aspect-ratio';
import FavouriteToggle from 'components/listing-card/favourite-toggle';
import ListingImage from 'components/listing-image';
import ListingStatus from 'components/listing-status';
import { listingCardIds } from 'constants/test-constants';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useCallback, useMemo, useRef } from 'react';
import { buildClassName } from 'utils/build-class-name';
import ComplianceImageOverlay from './components/compliance-image-overlay';
import styles from './style.module.scss';
import { ComplianceRequirement } from 'types/compliance';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import ComplianceOverlay from './components/compliance-overlay';
import PriceContainer from './components/price/PriceContainer';
import Timestamp from './components/timestamp';
import BedBathSqftLabels, { type BedBathSqftLabelsData } from './components/bed-bath-sqft-labels';
import Link from 'components/link';
import { DataAttributes, useIsomorphicLayoutEffect } from 'types/react';
import Address from './components/address';
import Provider from './components/provider';

import { ComingSoonBadge } from 'ui/badges';
import type { StandardStatus } from '@zoocasa/go-search';
import type { ComplianceProps, ListingCardData } from 'types/listing-card';
import type { PropertyFavouritedSource } from '@bedrock/analytics';


export const ADDRESS_NOT_AVAILABLE_LABEL = 'Address not available' as const;
export const SQFT_NOT_AVAILABLE_LABEL = 'N/A' as const;

// Re-export for backward compatibility
export type { ComplianceProps, ListingCardData } from 'types/listing-card';

export type DecorationsProps = {
  decorations?: {
    pvrDate?: string;
    comparableData?: BedBathSqftLabelsData;
  };
};

export type AnalyticsProps = {
  analytics?: {
    /** Source for property_favourited analytics (e.g. ADDRESS_PAGE, MAP_GRID, AREA_PAGE). */
    favouriteSource?: PropertyFavouritedSource;
  };
};

const DATA_HREF_ATTRIBUTE = 'data-href' as const;
const DATA_COMPLIANCE_REQUIREMENT_ATTRIBUTE = 'data-compliance-requirement' as const;
const DATA_LISTING_ID_ATTRIBUTE = 'data-listing-id' as const;
const DATA_PROVIDER_ID_ATTRIBUTE = 'data-provider-id' as const;
const DATA_PROVINCE_OR_STATE_ATTRIBUTE = 'data-adm1st' as const;

type MouseEventHandlerParams = Parameters<React.MouseEventHandler<HTMLElement>>;
export const CARD_CLICK_SOURCE = 'card' as const;
export const ADDRESS_CLICK_SOURCE = 'address' as const;
export const COMPLIANCE_OVERLAY_CLICK_SOURCE = 'compliance-overlay' as const;

export type ClickSource = typeof CARD_CLICK_SOURCE | typeof ADDRESS_CLICK_SOURCE | typeof COMPLIANCE_OVERLAY_CLICK_SOURCE;
export type ListingCardClickData = {
  listingId: number;
  complianceRequirement: ComplianceRequirement;
  providerId: number;
  stateOrProvince: string;
  href: string;
};

export type ListingCardProps = {
  listing: ListingCardData;

  /** Whether to use priority mode for loading images. If true, it will load the images in priority order. */
  usePriorityMode?: boolean;
  /**
   * The function to call when the listing card is clicked.
   *
   * Note: the click handler will call stopImmediatePropagation on the event object by default.
   * @param source The source of the click. Can be {@link CARD_CLICK_SOURCE} or {@link ADDRESS_CLICK_SOURCE}.
   * @param event The mouse event object that triggered the click handler.
   */
  onListingCardClick: (source: ClickSource, data: ListingCardClickData, ...args: MouseEventHandlerParams) => void;

} & ComplianceProps & DecorationsProps & AnalyticsProps;


type CardMediaProps = { blur: boolean; showOverlay: boolean; pvrDate: string; src: string; alt: string; id: number; timestamp?: Date | string | null; standardStatus?: StandardStatus | null; onMarketDate?: Date | string | null; providerId?: number | null };

//#region sub components
function PvrDate({ pvrDate }: { pvrDate: string }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatDateWithTemplate } = useI18nFormatting();

  return (
    <p className={styles['pvr-date']}>
      {t(LOCALE_COMMON_PATHS.LISTING_CARD_PVR_DATE_LABEL,
        {
          date: formatDateWithTemplate(pvrDate, 'MMM DD, YYYY'),
          defaultValue: 'Request sent on {{date}}',
        }
      )}
    </p>
  );
}

function CardMedia({ blur, showOverlay, pvrDate, src, alt, id, timestamp, standardStatus, onMarketDate, providerId }: CardMediaProps){
  const { t } = useTranslation(Namespaces.COMMON);
  const className = buildClassName(styles.image, blur && styles['blur-image']);

  const mediaAlt = t(LOCALE_COMMON_PATHS.LISTING_CARD_MEDIA_ALT,
    {
      alt,
      defaultValue: '{{alt}} | Card Image',
    }
  );

  const item = { image: src, isImageReady: src?.trim().length > 0, alt: mediaAlt, id: `${id}-card-image`, addedAt: timestamp };
  return (
    <div className={styles.row}>
      <AspectRatio ratio="4:3">
        <ListingImage
          item={item}
          isVisible={true}
          className={className}
          testId={listingCardIds.listingImage}
          usePriorityMode={true}
          isListingCardImage={true}
        />
        {showOverlay &&
          <ComplianceImageOverlay />
        }
        {pvrDate &&
          <PvrDate pvrDate={pvrDate} />
        }
        <ComingSoonBadge standardStatus={standardStatus} onMarketDate={onMarketDate} providerId={providerId} />
      </AspectRatio>
    </div>
  );
}
//#endregion


export default function ListingCard({ listing, compliance, onListingCardClick, decorations, analytics: { favouriteSource } = {}}: ListingCardProps) {
  const listingCardElement = useRef<HTMLDivElement>(null);
  const { id: listingId, thumbnailSrc: imageUrl, thumbnailAlt: imageDesc = '', isRental, providerId, province } = listing;
  const complianceRequirement = compliance?.complianceRequirement || ComplianceRequirement.NONE;
  const imageAlt = imageDesc && imageDesc.length > 0 ? imageDesc : 'Image';
  const showComplianceOverlay = complianceRequirement !== ComplianceRequirement.NONE;
  const requiresSignIn = complianceRequirement === ComplianceRequirement.SIGN_IN;
  const hideSquareFootage = compliance?.hideSquareFootage;
  const onListingCardClickRef = useRef(onListingCardClick);
  const analyticsProps = useMemo(() => ({
    source: favouriteSource,
  }), [favouriteSource]);

  useIsomorphicLayoutEffect(() => {
    onListingCardClickRef.current = onListingCardClick;
  }, [onListingCardClick]);

  const handleListingCardClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    const data: ListingCardClickData = {
      listingId: parseInt(e.currentTarget.getAttribute(DATA_LISTING_ID_ATTRIBUTE)),
      complianceRequirement: e.currentTarget.getAttribute(DATA_COMPLIANCE_REQUIREMENT_ATTRIBUTE) as ComplianceRequirement,
      providerId: parseInt(e.currentTarget.getAttribute(DATA_PROVIDER_ID_ATTRIBUTE)),
      stateOrProvince: e.currentTarget.getAttribute(DATA_PROVINCE_OR_STATE_ATTRIBUTE),
      href: e.currentTarget.getAttribute(DATA_HREF_ATTRIBUTE),
    };
    onListingCardClickRef.current?.(CARD_CLICK_SOURCE, data, e);
  }, []);

  const handleAddressClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    const data: ListingCardClickData = {
      listingId: parseInt(e.currentTarget.getAttribute(DATA_LISTING_ID_ATTRIBUTE)),
      complianceRequirement: e.currentTarget.getAttribute(DATA_COMPLIANCE_REQUIREMENT_ATTRIBUTE) as ComplianceRequirement,
      providerId: parseInt(e.currentTarget.getAttribute(DATA_PROVIDER_ID_ATTRIBUTE)),
      stateOrProvince: e.currentTarget.getAttribute(DATA_PROVINCE_OR_STATE_ATTRIBUTE),
      href: e.currentTarget.getAttribute(DATA_HREF_ATTRIBUTE),
    };
    onListingCardClickRef.current?.(ADDRESS_CLICK_SOURCE, data, e);
  }, []);

  const handleComplianceOverlayClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    const data: ListingCardClickData = {
      listingId: parseInt(e.currentTarget.getAttribute(DATA_LISTING_ID_ATTRIBUTE)),
      complianceRequirement: e.currentTarget.getAttribute(DATA_COMPLIANCE_REQUIREMENT_ATTRIBUTE) as ComplianceRequirement,
      providerId: parseInt(e.currentTarget.getAttribute(DATA_PROVIDER_ID_ATTRIBUTE)),
      stateOrProvince: e.currentTarget.getAttribute(DATA_PROVINCE_OR_STATE_ATTRIBUTE),
      href: e.currentTarget.getAttribute(DATA_HREF_ATTRIBUTE),
    };
    onListingCardClickRef.current?.(COMPLIANCE_OVERLAY_CLICK_SOURCE, data, e);
  }, []);

  const dataAttributes: DataAttributes = {
    [DATA_LISTING_ID_ATTRIBUTE]: listingId,
    [DATA_COMPLIANCE_REQUIREMENT_ATTRIBUTE]: complianceRequirement,
    [DATA_PROVIDER_ID_ATTRIBUTE]: providerId,
    [DATA_PROVINCE_OR_STATE_ATTRIBUTE]: province,
    [DATA_HREF_ATTRIBUTE]: listing.href,
  };
  return (
    <Link
      {...dataAttributes}
      href={listing.href}
      target="_blank"
      onClick={handleListingCardClick}
      className={styles.link}
    >
      <div
        className={buildClassName(styles.component, 'listing-card')}
        ref={listingCardElement}
        data-testid={listingCardIds.listingCard}
        {...dataAttributes}
      >
        <CardMedia
          id={listingId}
          src={imageUrl}
          alt={imageAlt}
          blur={showComplianceOverlay}
          showOverlay={requiresSignIn}
          timestamp={listing.timestamp}
          pvrDate={decorations?.pvrDate}
          standardStatus={listing.standardStatus}
          onMarketDate={listing.onMarketDate}
          providerId={providerId}
        />
        <div className={styles.wrapper}>
          {showComplianceOverlay ?
            <ComplianceOverlay
              complianceRequirement={complianceRequirement}
              onComplianceRequirementClick={handleComplianceOverlayClick}
              {...dataAttributes}
            />
            :
            <div className={styles['bottom-content']}>
              <div>
                <ListingStatus status={listing.standardStatus} isRental={isRental} className={styles.tag} />
                <FavouriteToggle listingId={listingId} analytics={analyticsProps} />
                <div className={styles['price-and-timestamp-row']} data-testid={listingCardIds.listingCardPrice}>
                  <PriceContainer standardStatus={listing.standardStatus} price={listing.price} soldPrice={listing.soldPrice} province={listing.province} testId={listingCardIds.listingPrice} />
                  <Timestamp timestamp={listing.timestamp} status={listing.standardStatus} isRental={isRental} testId={listingCardIds.listingTimeStamp} />
                </div>
                <div className={styles.row}>
                  <Address
                    {...dataAttributes}
                    fullAddress={listing.fullAddress}
                    street={listing.street}
                    city={listing.city}
                    province={listing.province}
                    position={listing.position}
                    onAddressClick={handleAddressClick} />
                </div>
                <BedBathSqftLabels listing={listing} listingToCompare={decorations?.comparableData} hideSquareFootage={hideSquareFootage}/>
              </div>
              <Provider src={listing.providerLogo} mlsNum={listing.mlsNum} providerId={listing.providerId} brokerage={listing.brokerage} />
            </div>
          }
        </div>
      </div>
    </Link>
  );
}